import { CdkScrollable } from '@angular/cdk/overlay';
import { AsyncPipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    DOCUMENT,
    ElementRef,
    inject,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterOutlet } from '@angular/router';
import { translate, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
    applyPortalLanguage,
    selectTranslate,
    AuthorityUtil,
    CommonUtil,
    CoreConstant,
    IUserKeyModel,
    Language,
    LocalStorageChange,
    LocalStorageService,
    LoginService,
    NavigationItemWithVisibility,
    Notification,
    NotificationCriteria,
    SessionTimeoutService,
    StringUtil,
    Theme,
    ThemeService,
    UrlPrivilegeUtilService,
    UserContextService,
    UserNotificationService,
    UserPreferenceService,
    UserService,
    WhoAmI,
    ZendeskWidgetService,
} from '@libs/vc-core-lib';
import { ChangePasswordDialogComponent, HeaderComponent } from '@libs/vc-header-lib';
import { IOrgModel, OrgService } from '@libs/vc-org-lib';
import { delay, finalize, map, Observable, of, Subject, take, takeUntil } from 'rxjs';
import {
    DialogComponent,
    LocaleService,
    NotificationComponent,
    NotificationService,
    SideNavigationComponent,
} from 'vcp-common-ui-lib';

interface PendoInstance {
    initialize(config: { visitor: { id?: string; email?: string }; account: { id?: string } }): void;
}

declare let pendo: PendoInstance;

@Component({
    styleUrls: ['./main-view.component.scss'],
    templateUrl: './main-view.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TranslocoPipe,
        NotificationComponent,
        HeaderComponent,
        AsyncPipe,
        SideNavigationComponent,
        RouterOutlet,
        ChangePasswordDialogComponent,
        DialogComponent,
        CdkScrollable,
    ],
})
export class MainViewComponent implements OnInit, OnDestroy {
    protected readonly sessionTimeout: SessionTimeoutService = inject(SessionTimeoutService);
    private readonly _urlPrivilegeUtilService: UrlPrivilegeUtilService = inject(UrlPrivilegeUtilService);
    private readonly _loginService: LoginService = inject(LoginService);
    private readonly _orgService: OrgService = inject(OrgService);
    private readonly _userService: UserService = inject(UserService);
    private readonly _userContextService: UserContextService = inject(UserContextService);
    private readonly _userPreferenceService: UserPreferenceService = inject(UserPreferenceService);
    private readonly _userNotificationService: UserNotificationService = inject(UserNotificationService);
    private readonly _localStorageService: LocalStorageService = inject(LocalStorageService);
    private readonly _notificationToastService: NotificationService = inject(NotificationService);
    private readonly _changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
    private readonly _themeService: ThemeService = inject(ThemeService);
    private readonly _zendeskWidgetService: ZendeskWidgetService = inject(ZendeskWidgetService);
    private readonly _localeService: LocaleService = inject(LocaleService);
    private readonly _transloco: TranslocoService = inject(TranslocoService);
    private readonly _document: Document = inject(DOCUMENT);
    private readonly _router: Router = inject(Router);
    private readonly _dialogRef: MatDialog = inject(MatDialog);
    private readonly _destroyRef: DestroyRef = inject(DestroyRef);
    private readonly _destroy$: Subject<void> = new Subject();

    public userFullName: string = '';

    public loading: boolean = true;
    public usingMfa: boolean = false;
    public isImpersonatingRole: boolean = false;
    public forcePasswordChangeDialog: boolean = false;
    public pauseForLogout: boolean = false;
    public isOrgAdmin: boolean = false;

    public navigationTree: NavigationItemWithVisibility[] = [];
    public orgModels$!: Observable<IOrgModel[]>;
    public user!: WhoAmI;
    public theme!: Theme | null;

    public notifications$!: Observable<Notification[]>;

    @ViewChild('mainContent')
    mainContent!: ElementRef;

    constructor() {
        CommonUtil.showAppLoader();
        selectTranslate(this._transloco, () => this._refreshNavigationMenu(), this._destroyRef);
    }

    ngOnInit() {
        this.sessionTimeout.expired$.pipe(takeUntil(this._destroy$)).subscribe(() => this._redirectToLogin());

        // Listen to localStorage changes across tabs for the auth token and logout if it is null or empty
        this._localStorageService
            .getChanges(CoreConstant.X_AUTH_TOKEN)
            .pipe(takeUntil(this._destroy$))
            .subscribe((change: LocalStorageChange) => {
                if (change.currentValue === null || change.currentValue === '') {
                    this.logout();
                }
            });

        // Listen to user context changes and update the user state
        this._userContextService
            .getContext()
            .pipe(takeUntil(this._destroy$))
            .subscribe((user: WhoAmI | null) => {
                if (!user) return;

                this.user = user;
                this.usingMfa = user.usingMfa ?? false;
                this.isOrgAdmin = user.hasAuthority(AuthorityUtil.ROLE_ORG_ADMIN);

                const storedLang = localStorage.getItem(CoreConstant.VC_LANGUAGE_ABBREVIATION) as Language | null;
                const effectiveLang = storedLang ?? (user.locale as Language) ?? Language.EN;
                if (this._transloco.getActiveLang() !== effectiveLang) {
                    applyPortalLanguage(effectiveLang, this._transloco, this._localeService);
                }
                this.user.locale = effectiveLang;
                this._document.documentElement.lang = effectiveLang;
                this._checkImpersonation();
                this._getNotificationHistory();
                this._getOrgs();
                this._initPendo(user);
                this._initZendesk(user);
                this._refreshNavigationMenu();

                const userKey: IUserKeyModel = {
                    username: user.getUsername(),
                    authenticator: user.getAuthenticator(),
                } as IUserKeyModel;

                this._userPreferenceService
                    .initializeUserPreferences(userKey)
                    .pipe(
                        delay(500),
                        take(1),
                        finalize(() => {
                            this.forcePasswordChangeDialog =
                                !this.isImpersonatingRole && (!!user.forcePasswordChange || !!user.credentialsExpired);
                            this.pauseForLogout = this.forcePasswordChangeDialog;

                            if (this.pauseForLogout) {
                                this.sessionTimeout.setPaused(true);
                            } else {
                                this.sessionTimeout.start();
                            }

                            this.theme = this._themeService.getTheme();
                            CommonUtil.hideAppLoader();
                            this.loading = false;
                            this._changeDetectorRef.markForCheck();
                        })
                    )
                    .subscribe({
                        error: () => {
                            this._notificationToastService.error(translate('ERROR.FETCHING_USER_PREFERENCES'));
                        },
                    });
            });
    }

    public staySignedIn(): void {
        this.sessionTimeout
            .staySignedIn()
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                error: () => {
                    this._redirectToLogin();
                },
            });
    }

    public onSessionLogout(): void {
        this._redirectToLogin();
    }

    public logout() {
        CommonUtil.showAppLoader();
        if (this.isImpersonatingRole) {
            this.endImpersonation();
        } else {
            this._loginService.logout().pipe(take(1)).subscribe({
                next: this._clearSessionAndLogout,
                error: this._clearSessionAndLogout,
            });
        }
    }

    public changeTheme(theme: Theme) {
        this._themeService.setTheme(theme);
        this._userPreferenceService.saveUserTheme(theme).pipe(take(1)).subscribe();
    }

    public changeLang(lang: Language) {
        if (this.user) {
            this.user.locale = lang;
        }
        applyPortalLanguage(lang, this._transloco, this._localeService);
        this._saveUserLanguage(lang);
        this._changeDetectorRef.markForCheck();
    }

    public selectOrg(id: string) {
        this.loading = true;
        this._orgService
            .setOrgForSwitch(id)
            .pipe(
                take(1),
                finalize(() => {
                    this.loading = false;
                    this._changeDetectorRef.markForCheck();
                })
            )
            .subscribe(() => {
                window.location.reload();
            });
    }

    public endImpersonation() {
        CommonUtil.showAppLoader();
        this._loginService.logout().pipe(take(1)).subscribe({
            next: this._logoutImpersonateUser,
            error: this._logoutImpersonateUser,
        });
    }

    public skipToMainContent(): void {
        this.mainContent?.nativeElement?.focus();
    }

    public readonly updateForcePasswordChange = (success: boolean) => {
        if (success) {
            this.forcePasswordChangeDialog = false;
            this.user.forcePasswordChange = false;
            this._changeDetectorRef.markForCheck();
            CommonUtil.showAppLoader();

            // Pause for 3 seconds to allow the user to see the success message
            setTimeout(() => {
                this.logout();
                this._changeDetectorRef.markForCheck();
            }, 3000);
        }
    };

    private readonly _checkImpersonation = () => {
        this.isImpersonatingRole = this.user.hasAuthority(AuthorityUtil.PERMISSION_ROLE_PREVIOUS_IMPERSONATOR);
        this.userFullName = '';

        if (this.isImpersonatingRole) {
            if (this.user) {
                if (StringUtil.isBlankWithTrim(this.user.getUserFullName())) {
                    this.userFullName = this.user.username ?? '';
                } else {
                    this.userFullName = `${this.user.getUserFullName()}`;
                }
            }
        }
    };

    private readonly _getOrgs = () => {
        this._orgService
            .getOrgById(this.user.getOrgId())
            .pipe(takeUntil(this._destroy$))
            .subscribe((orgModel: IOrgModel) => {
                if (orgModel?.name) {
                    this.orgModels$ = this._orgService.getOrgsForSwitch();
                } else {
                    this.orgModels$ = of();
                }
            });
    };

    private readonly _refreshNavigationMenu = (): void => {
        if (!this.user) {
            return;
        }

        this._getNavigationMenu(this.user);
        this._changeDetectorRef.markForCheck();
    };

    private readonly _getNavigationMenu = (model: WhoAmI) => {
        const menuItems: NavigationItemWithVisibility[] = [];
        const visibleItems: NavigationItemWithVisibility[] = this._urlPrivilegeUtilService.getVisibleURLPrivileges();

        visibleItems.forEach((menu: NavigationItemWithVisibility) => {
            if (menu?.children && menu?.children?.length > 0) {
                const parentAuthority = this._hasAuthority(menu.privileges, model);
                if (parentAuthority) {
                    const parent = menu;
                    const children = [...menu.children];
                    parent.children = [];
                    children?.forEach((childMenu: NavigationItemWithVisibility) => {
                        if (this._hasAuthority(childMenu.privileges, model) && childMenu.visible) {
                            parent.children?.push(childMenu);
                        }
                    });

                    menuItems.push(parent);
                }
            } else if (this._hasAuthority(menu.privileges, model) && menu.visible) {
                menuItems.push(menu);
            }
        });

        this.navigationTree = menuItems;
    };

    private _hasAuthority(listPrivileges: string[] | undefined, model: WhoAmI): boolean {
        if (!listPrivileges) {
            return false;
        }

        return listPrivileges.some((value: string) => (model?.authorities?.indexOf(value) ?? -1) > -1);
    }

    private _redirectToLogin(): void {
        this.sessionTimeout.stop();
        this._userContextService.clear();
        this._userPreferenceService.clear();
        this._closeSpinnersAndDialogs();
        CommonUtil.showAppLoader();
        this._router.navigate(['login']).then();
    }

    private _getNotificationHistory() {
        if (!this.user) {
            return;
        }

        const criteria: NotificationCriteria = {
            recipient: {
                username: this.user.username ?? '',
            },
        } as NotificationCriteria;

        this.notifications$ = this._userNotificationService
            .getNotifications(criteria)
            .pipe(
                map((data: Notification[]) =>
                    data?.filter((value: Notification) => value.shortSubject && value.shortDescription)
                )
            );
    }

    private readonly _clearSessionAndLogout = () => {
        this._closeZendesk();
        this._userContextService.clear();
        this._userPreferenceService.clear();
        this._closeSpinnersAndDialogs();
        this._router.navigate(['login']).then();
    };

    private readonly _logoutImpersonateUser = () => {
        this._closeZendesk();
        this._userContextService.clearSessionOnly();
        this._userPreferenceService.clear();
        this._closeSpinnersAndDialogs();
        this._router.navigate(['login']).then();
    };

    private readonly _closeSpinnersAndDialogs = () => {
        this._dialogRef.closeAll();
        this.loading = false;
        this._changeDetectorRef.markForCheck();
    };

    private readonly _initPendo = (user: WhoAmI) => {
        pendo.initialize({
            visitor: {
                id: user?.username,
                email: user?.email,
            },
            account: {
                id: user?.getOrgId(),
            },
        });
    };

    private readonly _initZendesk = (user: WhoAmI) => {
        this._zendeskWidgetService.initializeForUser(user);
    };

    private readonly _closeZendesk = () => {
        this._zendeskWidgetService.close();
    };

    private readonly _saveUserLanguage = (lang: Language) => {
        if (!this?.user?.username) {
            return;
        }

        this._userService.saveUserLocale(this.user.username, lang).pipe(take(1)).subscribe();
    };

    mainContentFocus(): void {
        const mainContent: HTMLElement | null = document.getElementById('vc-main');
        if (mainContent) {
            mainContent.scrollTop = 0;
            mainContent.focus();
        }
    }

    ngOnDestroy(): void {
        this.sessionTimeout.stop();
        this._destroy$.next();
        this._destroy$.complete();
    }
}
